import { ScrollAnimContext } from "@/contexts/ScrollAnimContext";
import { useFollowPointer } from "@/libs/AnimationHelper";
import { useScroll, motion, useTransform, easeInOut, cubicBezier, useMotionValueEvent, type SpringOptions } from "motion/react";
import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { SocialLink, type SocialPlatforms } from "../SocialLink";
import { AnimatedSectionsHeading } from "./AnimatedSectionsHeading";

export default function ContactSection({ ref }: { ref?: RefObject<HTMLDivElement | null> }) {
  // Configure button springiness
  const btnSpring: SpringOptions = { damping: 10, stiffness: 50, mass: 0.2, restDelta: 0.001 };
  const btnRef = useRef(null);
  const btnConstraints = { top: -10, left: -10, right: 10, bottom: 10 };
  const btnStyles = useFollowPointer(btnSpring, btnRef, btnConstraints);

  const container = ref || useRef(null);

  const { scrollYProgress } = useScroll({ target: container, offset: ["start start", "end end"] }); // Progress starts when the section has completely filled the viewport
  const backgroundColor = useTransform(scrollYProgress, [0.2, 0.3], ["rgba(248, 248, 248, 0)", "rgba(248, 248, 248, 1)"]);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.3], [0, 1], { ease: easeInOut });
  const btnOpacity = useTransform(scrollYProgress, [0.35, 0.58], [0, 1], { ease: easeInOut });

  // Render social links
  const socials: SocialPlatforms[] = ["Github", "LinkedIn"];
  const socialLinks = socials.map((platform, i) => {
    // Fade in animation styles for the button
    const y = useTransform(scrollYProgress, [0.65 + i * 0.05, 0.8 + i * 0.05], [50, 0], { ease: cubicBezier(0.4, 0, 0.2, 1) });
    const opacity = useTransform(scrollYProgress, [0.65 + i * 0.05, 0.8 + i * 0.05], [0, 1], { ease: cubicBezier(0.4, 0, 0.2, 1) });

    return <SocialLink key={platform} platform={platform} style={{ y, opacity }} />;
  });

  return (
    <ScrollAnimContext.Provider value={scrollYProgress}>
      <motion.div style={{ height: "300svh", marginTop: "-280svh", backgroundColor }} className={"w-full text-black"} ref={container}>
        <div style={{ height: "100svh" }} className='sticky top-0 flex justify-center items-center'>
          <div>
            {/* "Let's embark on one together" */}
            <AnimatedSectionsHeading>
              <motion.div className='text-center font-semibold' style={{ opacity: titleOpacity }}>
                <h2>Let's embark on one together</h2>
              </motion.div>
            </AnimatedSectionsHeading>

            <motion.div style={{ opacity: btnOpacity }} className='border border-gray-600 border-dashed rounded-xl w-fit mx-auto mt-16'>
              <motion.a
                style={btnStyles}
                className='btn btn-primary btn-lg lg:btn-xl items-center rounded-xl'
                ref={btnRef}
                href='mailto:xavier.workstuff@gmail.com'
              >
                Contact Me
              </motion.a>
            </motion.div>
            <motion.div className='flex justify-around pt-20 md:pt-32 mx-auto'>{socialLinks}</motion.div>
          </div>
        </div>
      </motion.div>
    </ScrollAnimContext.Provider>
  );
}
